import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { GetDeviceStatus } from "../Api";
import isBetween from "dayjs/plugin/isBetween";
import TimeRangePickerPopover from "./TimeRangePickerPopover";
dayjs.extend(isBetween);

interface Props {
  selectedDevice: {
    deviceID: string;
    name?: string;
  };
}

interface StatusData {
  time: string;
  status: number;
}

/**
 * A timeline component to visualize device connectivity status within a selected time range.
 */
const TimelineWithPicker: React.FC<Props> = ({ selectedDevice }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [range, setRange] = useState<{ from: Date; to: Date } | null>(null);
  const [timezone, setTimezone] = useState<string>(dayjs.tz.guess());
  const [statusData, setStatusData] = useState<StatusData[]>([]);
  const [loading, setLoading] = useState(false);

  /**
   * Fetches device status data within a given date range.
   *
   * @param {string} deviceID - Device ID to query.
   * @param {Date} from - Start time.
   * @param {Date} to - End time.
   */
  const fetchStatus = async (deviceID: string, from: Date, to: Date) => {
    setLoading(true);
    try {
      const data = await GetDeviceStatus(
        deviceID,
        from.toISOString(),
        to.toISOString()
      );
      if (Array.isArray(data.data)) {
        setStatusData(data.data);
      } else {
        console.error("Invalid data from status API", data.data);
        setStatusData([]);
      }
    } catch (err) {
      console.error("Failed to fetch device status", err);
      setStatusData([]);
    }
    setLoading(false);
  };

  // Re-fetch data when device or time range changes
  useEffect(() => {
    if (!selectedDevice?.deviceID || !range) return;
    fetchStatus(selectedDevice.deviceID, range.from, range.to);
  }, [selectedDevice?.deviceID, range]);

  /**
   * Callback for when a new time range is applied from the picker.
   *
   * @param {{ from: Date, to: Date }} r - The selected date range.
   */
  const handleRangeApply = (r: { from: Date; to: Date; timezone: string }) => {
    setRange(r);
    setTimezone(r.timezone);
  };

  /**
   * Generates chart data blocks (connected/disconnected periods) based on the status data.
   *
   * @returns {any[]} ApexCharts-compatible data for rendering timeline bars.
   */
  const generateStatusBlocks = (): any[] => {
    if (!statusData?.length) return [];

    const thresholdSeconds = 90;
    const now = dayjs().tz(timezone);

    const filtered = statusData
      .map((d) => ({ ...d, time: dayjs(d.time).tz(timezone) }))
      .sort((a, b) => a.time.unix() - b.time.unix());

    const blocks: any[] = [];

    let start = filtered[0].time;

    for (let i = 1; i < filtered.length; i++) {
      const prev = filtered[i - 1].time;
      const curr = filtered[i].time;
      const diff = curr.diff(prev, "second");

      if (diff > thresholdSeconds) {
        // Previous block ends, gap starts
        blocks.push({
          x: "Connected",
          y: [start.valueOf(), prev.valueOf()],
          fillColor: "#00b894",
        });

        // Disconnected period between prev and current
        blocks.push({
          x: "Disconnected",
          y: [
            prev.add(1, "second").valueOf(),
            curr.subtract(1, "second").valueOf(),
          ],
          fillColor: "#d63031",
        });

        // Start a new connected block
        start = curr;
      }
    }

    // Final connected block
    blocks.push({
      x: "Connected",
      y: [start.valueOf(), filtered.at(-1)?.time.valueOf()],
      fillColor: "#00b894",
    });

    // Check if there's a gap between last ping and now
    const lastPing = filtered.at(-1)?.time;
    if (lastPing && now.diff(lastPing, "second") > thresholdSeconds) {
      blocks.push({
        x: "Disconnected",
        y: [lastPing.add(1, "second").valueOf(), now.valueOf()],
        fillColor: "#d63031",
      });
    }

    return blocks;
  };

  const series = [
    {
      data: range ? generateStatusBlocks() : [],
    },
  ];

  const options: ApexOptions = {
    chart: {
      type: "rangeBar",
      height: 300,
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "40%",
      },
    },
    grid: {
      padding: {
        right: 20,
      },
    },
    // xaxis: {
    //   type: "datetime",
    //   labels: {
    //     datetimeUTC: false,
    //   },
    // },
    xaxis: {
      type: "datetime",
      labels: {
        formatter: (val) => {
          return dayjs.tz(val, timezone).format("HH:mm");
        },
      },
      title: {
        text: `Time (${timezone})`,
      },
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const item = w.config.series[seriesIndex].data[dataPointIndex];
        const from = dayjs(item.y[0])
          .tz(timezone)
          .format("DD-MMM-YYYY HH:mm:ss");
        const to = dayjs(item.y[1]).tz(timezone).format("DD-MMM-YYYY HH:mm:ss");

        return `
        <div style="padding:8px;">
          <strong>${item.x}</strong><br />
          From: ${from}<br />
          To: ${to}
        </div>
      `;
      },
    },
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box
        sx={{
          mb: { xs: "2rem", md: "4rem" },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Device Timeline - {selectedDevice?.deviceID}
        </Typography>
        <TimeRangePickerPopover onApply={handleRangeApply} />
      </Box>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>Loading data...</Box>
      ) : range && series[0].data.length > 0 ? (
        <Box sx={{ overflow: isMobile ? "auto" : "none" }}>
          <ReactApexChart
            options={options}
            series={series}
            type="rangeBar"
            height={300}
            width={isMobile ? 320 : ""}
          />
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", mt: 4, color: "gray" }}>
          Please select a time range or no data available.
        </Box>
      )}
    </Box>
  );
};

export default TimelineWithPicker;
